import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <div className={`prose prose-invert prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        // Customize heading styles
        h1: ({ node, ...props }) => <h1 className="text-2xl font-bold text-white mb-4 mt-6" {...props} />,
        h2: ({ node, ...props }) => <h2 className="text-xl font-bold text-slate-200 mb-3 mt-5" {...props} />,
        h3: ({ node, ...props }) => <h3 className="text-lg font-semibold text-slate-300 mb-2 mt-4" {...props} />,
        h4: ({ node, ...props }) => <h4 className="text-base font-semibold text-slate-300 mb-2 mt-3" {...props} />,
        
        // Customize paragraph
        p: ({ node, ...props }) => <p className="text-slate-300 leading-relaxed mb-3" {...props} />,
        
        // Customize lists
        ul: ({ node, ...props }) => <ul className="list-disc list-inside text-slate-300 mb-3 space-y-1" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-slate-300 mb-3 space-y-1" {...props} />,
        li: ({ node, ...props }) => <li className="text-slate-300 leading-relaxed" {...props} />,
        
        // Customize links
        a: ({ node, ...props }) => (
          <a 
            className="text-blue-400 hover:text-blue-300 underline transition-colors" 
            target="_blank" 
            rel="noopener noreferrer" 
            {...props} 
          />
        ),
        
        // Customize code blocks
        code: ({ node, inline, ...props }: any) => 
          inline ? (
            <code className="bg-slate-800 text-emerald-400 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
          ) : (
            <code className="block bg-slate-800 text-slate-300 p-3 rounded-lg overflow-x-auto text-sm font-mono mb-3" {...props} />
          ),
        
        // Customize blockquotes
        blockquote: ({ node, ...props }) => (
          <blockquote className="border-l-4 border-blue-500 pl-4 italic text-slate-400 my-3" {...props} />
        ),
        
        // Customize tables
        table: ({ node, ...props }) => (
          <div className="overflow-x-auto mb-3">
            <table className="min-w-full border border-slate-700 rounded-lg" {...props} />
          </div>
        ),
        thead: ({ node, ...props }) => <thead className="bg-slate-800" {...props} />,
        th: ({ node, ...props }) => <th className="border border-slate-700 px-3 py-2 text-left text-slate-300 font-semibold" {...props} />,
        td: ({ node, ...props }) => <td className="border border-slate-700 px-3 py-2 text-slate-300" {...props} />,
        
        // Customize horizontal rule
        hr: ({ node, ...props }) => <hr className="border-slate-700 my-4" {...props} />,
        
        // Customize strong/bold
        strong: ({ node, ...props }) => <strong className="font-bold text-white" {...props} />,
        
        // Customize emphasis/italic
        em: ({ node, ...props }) => <em className="italic text-slate-300" {...props} />,
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
